import React, { useState, useEffect } from "react";
import { Drawer } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const drawerWidth = 420;

function AdvanceSearchDrawer({
  open,
  handleClose,
  initialFilters = {},
  onApplyFilters,
}) {
  const [listingType, setListingType] = useState(initialFilters.listingType || "All");
  const [location, setLocation] = useState(initialFilters.location || "Badagry");
  const [minPrice, setMinPrice] = useState(initialFilters.minPrice || "2,000,000");
  const [maxPrice, setMaxPrice] = useState(initialFilters.maxPrice || "3,000,000");
  const [dateAdded, setDateAdded] = useState(
    initialFilters.dateAdded || "18th June 2026"
  );

  useEffect(() => {
    if (open) {
      setListingType(initialFilters.listingType || "All");
      setLocation(initialFilters.location || "Badagry");
      setMinPrice(initialFilters.minPrice || "2,000,000");
      setMaxPrice(initialFilters.maxPrice || "3,000,000");
      setDateAdded(initialFilters.dateAdded || "18th June 2026");
    }
  }, [open, initialFilters]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onApplyFilters) {
      onApplyFilters({
        listingType,
        location,
        minPrice,
        maxPrice,
        dateAdded,
      });
    }
    handleClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: { xs: "100%", sm: drawerWidth },
          backgroundColor: "white",
          borderTopLeftRadius: "12px",
          borderBottomLeftRadius: "12px",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.08)",
        },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-4 bg-[#F8FAFC] border-b border-gray-100 flex items-center justify-between">
          <p className="text-[#1E222B] text-[15px] font-medium">
            Advanced Search option
          </p>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-700 transition p-1 rounded-full hover:bg-gray-200"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSearch} className="p-6 flex-1 flex flex-col justify-between overflow-y-auto">
          <div className="space-y-6">
            {/* Listing Type */}
            <div>
              <label className="text-sm font-medium text-gray-800 block mb-3">
                Listing Type
              </label>
              <div className="flex items-center gap-2.5">
                {["All", "Property", "Land"].map((type) => {
                  const isActive = listingType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setListingType(type)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                        isActive
                          ? "bg-[#00BC67] text-white shadow-xs"
                          : "bg-[#E6F8F0] text-gray-800 hover:bg-[#d5f5e6]"
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="text-sm font-medium text-gray-800 block mb-2">
                Location
              </label>
              <div className="relative">
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full bg-[#F8FAFC] text-gray-700 text-sm rounded-xl py-3.5 px-4 pr-10 appearance-none focus:outline-none focus:ring-1 focus:ring-[#00BC67] cursor-pointer"
                >
                  <option value="Badagry">Badagry</option>
                  <option value="Lekki Phase 1">Lekki Phase 1</option>
                  <option value="Ikoyi">Ikoyi</option>
                  <option value="Victoria Island">Victoria Island</option>
                  <option value="Ibeju Lekki">Ibeju Lekki</option>
                  <option value="Epe">Epe</option>
                  <option value="Ajah">Ajah</option>
                </select>
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600">
                  <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                </div>
              </div>
            </div>

            {/* Min Price & Max Price */}
            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <label className="text-sm font-medium text-gray-800 block mb-2">
                  Min. Price
                </label>
                <input
                  type="text"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="2,000,000"
                  className="w-full bg-[#F8FAFC] text-gray-700 text-sm rounded-xl py-3.5 px-4 focus:outline-none focus:ring-1 focus:ring-[#00BC67]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-800 block mb-2">
                  Max. Price
                </label>
                <input
                  type="text"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="3,000,000"
                  className="w-full bg-[#F8FAFC] text-gray-700 text-sm rounded-xl py-3.5 px-4 focus:outline-none focus:ring-1 focus:ring-[#00BC67]"
                />
              </div>
            </div>

            {/* Date added to platform */}
            <div>
              <label className="text-sm font-medium text-gray-800 block mb-2">
                Date added to platform
              </label>
              <div className="relative">
                <select
                  value={dateAdded}
                  onChange={(e) => setDateAdded(e.target.value)}
                  className="w-full bg-[#F8FAFC] text-gray-700 text-sm rounded-xl py-3.5 px-4 pr-10 appearance-none focus:outline-none focus:ring-1 focus:ring-[#00BC67] cursor-pointer"
                >
                  <option value="18th June 2026">18th June 2026</option>
                  <option value="05 Jan 2025">05 Jan 2025</option>
                  <option value="Last 7 Days">Last 7 Days</option>
                  <option value="Last 30 Days">Last 30 Days</option>
                  <option value="This Year">This Year</option>
                  <option value="All Time">All Time</option>
                </select>
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600">
                  <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                </div>
              </div>
            </div>
          </div>

          {/* Search Button */}
          <div className="pt-6 mt-6">
            <button
              type="submit"
              className="w-full bg-[#00BC67] hover:bg-[#00a85c] text-white font-medium py-3.5 rounded-xl text-center text-sm transition-all shadow-sm active:scale-[0.99] cursor-pointer"
            >
              Search
            </button>
          </div>
        </form>
      </div>
    </Drawer>
  );
}

export default AdvanceSearchDrawer;
